import {
  Alert,
  Backdrop,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  LinearProgress,
  Modal,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material';
import ModalGeneric from '../../Components/generic/ModalGeneric';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import { InformacoesCliente } from '../../types/EdClienteTypes';
import apiAutenticacao from '../../api/apiAutenticacao';
import { useDispatch } from 'react-redux';
import { CODIGO_CONFIRMACAO_VIA_EMAIL, CODIGO_CONFIRMACAO_VIA_WHATSAPP } from '../../constants';
import { loginAction } from '../../redux/sessao/sessaoActions';
import { mascararEmail, mascararTelefone, validarDocumento } from '../../helper';
import { useSessao } from '../../redux/sessao/sessaoHooks';

type Props = {
  open: boolean;
  onClose: () => void;
};

const ModalRecuperarSenha: React.FC<Props> = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [documento, setDocumento] = useState('');
  const [clienteEncontrado, setClienteEncontrado] = useState<InformacoesCliente | null>(null);
  const [openModalRecuperarSenha, setOpenModalRecuperarSenha] = useState(false);
  const [apiEmailOk, setApiEmailOk] = useState(false);
  const [apiZapOk, setApiZapOk] = useState(false);
  const [semOpcaoDisponivel, setSemOpcaoDisponivel] = useState(false);
  const [opcao, setOpcao] = useState(CODIGO_CONFIRMACAO_VIA_EMAIL);

  const [telefoneSelecionado, setTelefoneSelecionado] = useState('');
  const [pedirCodigo, setPedirCodigo] = useState(false);
  const [codigoConfirmacao, setCodigoConfirmacao] = useState('');
  const [contador, setContador] = useState(0);

  const verificarCliente = async () => {
    if (!validarDocumento(documento)) {
      toast.error('CPF ou CNPJ inválido');
      return;
    }
    setLoading(true);

    try {
      const res = await apiAutenticacao.buscaDadosCliente(documento);

      if (!res) {
        toast.error(`Cliente não encontrado para o documento: ${documento}`);
        console.error('Cliente não encontrado para o documento:', documento);
      }

      if (!res.nome) {
        toast.error('Erro ao buscar dados do cliente. Entre em contato com a empresa.');
        console.error('Dados inválidos do cliente:', res);
        return;
      }

      let resEmail = false;
      let resZap = false;

      try {
        resEmail = await apiAutenticacao.getStatusEmail();
      } catch (e) {
        console.warn('Erro ao verificar email:', e);
        resEmail = false;
      }

      try {
        const statusZap = await apiAutenticacao.getStatusWhatsApp();
        resZap = statusZap === 'OK';
      } catch (e) {
        console.warn('WhatsApp indisponível:', e);
        resZap = false;
      }

      setApiEmailOk(resEmail);
      setApiZapOk(resZap);

      setClienteEncontrado(res);
      setOpenModalRecuperarSenha(true);
      setLoading(false);
      return;
    } catch (erro: any) {
      toast.error('Erro ao verificar cliente. Tente novamente mais tarde.');
      console.error('Erro ao buscar dados do cliente:', erro);
    } finally {
      setLoading(false);
    }
  };
  const dispatch = useDispatch();

  useEffect(() => {
    if ((!apiZapOk && !apiEmailOk) || (!apiZapOk && clienteEncontrado?.email)) {
      setSemOpcaoDisponivel(true);
    }
  }, [apiEmailOk, apiZapOk, clienteEncontrado?.email]);

  const iniciarTimer = () => {
    setContador(120);
  };

  const enviarCodigoVerificacao = async () => {
    try {
      if (!opcao || opcao === 0) {
        toast.error('Escolha a opção para enviar o código de recuperação.');
        return;
      }

      if (opcao === CODIGO_CONFIRMACAO_VIA_WHATSAPP && !telefoneSelecionado) {
        toast.error(
          'Por favor selecione o número de WhatsApp que deseja receber o código de recuperação'
        );
        return;
      }

      if (!clienteEncontrado) {
        toast.error('Erro: Cliente não encontrado.');
        return;
      }

      setLoading(true);

      await apiAutenticacao.enviarCodigoRecuperacaoSenha(
        opcao,
        clienteEncontrado,
        telefoneSelecionado
      );

      toast.info('Código de recuperação enviado');
      setPedirCodigo(true);
      iniciarTimer();
    } catch (erro: any) {
      toast.error('Erro');
    } finally {
      setLoading(false);
    }
  };

  const confirmarCodigoVerificacao = async () => {
    try {
      if (!clienteEncontrado) {
        toast.error('Cliente não encontrado.');
        return;
      }
      const res = await apiAutenticacao.confirmarCodigoRecuperacaoSenha(
        codigoConfirmacao,
        clienteEncontrado
      );
      toast.success('Código de recuperação confirmado!');
      const action = loginAction(res);
      dispatch(action);
      localStorage.setItem('sessao', JSON.stringify(res));
    } catch (erro: any) {
      if (erro.response?.data?.includes('Código expirado')) {
        toast.error('Código expirado! Solicite um novo.');
      } else {
        toast.error(erro.response?.data || 'Erro ao verificar código de recuperação.');
      }
    }
  };
  useEffect(() => {
    if (contador <= 0) return;
    const id = setTimeout(() => setContador((prev) => prev - 1), 1000);
    if (contador === 1) {
      toast.error('Código de recuperação expirado! Solicite um novo.');
      onClose();
    }
    return () => clearTimeout(id);
  }, [contador]);

  if (loading) {
    return (
      <ModalGeneric open={open} onClose={onClose}>
        <Box sx={{ width: '100%', textAlign: 'center' }}>
          <h2>Verificando informações ...</h2>
          <LinearProgress />
        </Box>
      </ModalGeneric>
    );
  }

  const podeReceberCodigo =
    (apiEmailOk && clienteEncontrado?.email?.trim()) ||
    (apiZapOk && clienteEncontrado?.telefone?.some((t) => t && t.trim() && t !== '00000000000'));

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition>
      <Box
        sx={{
          position: 'absolute' as const,
          top: '50%',
          left: '50%',
          display: 'flex',
          transform: 'translate(-50%, -50%)',
          width: '100%',
          maxWidth: 390,
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 5,
          p: 3,
          flexDirection: 'column',
          minWidth: 300,
          alignItems: 'center'
        }}
      >
        <Typography
          variant="h5"
          sx={{
            color: '#428CFF',
            fontWeight: 700,
            fontSize: '1.4rem',
            mb: 1,
            letterSpacing: '0.5px'
          }}
        >
          Recuperação de senha
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: '#666',
            mb: 2,
            fontSize: 13
          }}
        >
          Siga os passos para redefinir sua senha
        </Typography>
        <TextField
          label="CPF / CNPJ"
          variant="outlined"
          size="small"
          value={documento}
          onChange={(e) => setDocumento(e.target.value.replace(/\D/g, '').slice(0, 14))}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <HistoryEduIcon fontSize="small" />
              </InputAdornment>
            )
          }}
          sx={{
            width: ' 100%',
            maxWidth: 200,
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: '#fff'
            }
          }}
        />
        {!clienteEncontrado && !openModalRecuperarSenha && (
          <Grid item xs={12} sx={{ textAlign: 'center', marginTop: 1 }}>
            <Button fullWidth variant="contained" onClick={verificarCliente}>
              <Typography fontSize={12}>Recuperar Senha</Typography>
            </Button>
          </Grid>
        )}

        {clienteEncontrado &&
          openModalRecuperarSenha &&
          (podeReceberCodigo ? (
            <Box sx={{ width: '100%', mt: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'left', fontWeight: 500 }}>
                Escolha como receber o código:
              </Typography>
              <FormControl fullWidth>
                <RadioGroup
                  aria-labelledby="radio-group"
                  defaultValue={opcao}
                  name="radio-buttons-group"
                  sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
                >
                  {apiEmailOk && clienteEncontrado.email && clienteEncontrado.email !== ' ' && (
                    <FormControlLabel
                      value={CODIGO_CONFIRMACAO_VIA_EMAIL}
                      control={<Radio sx={{ '& .MuiSvgIcon-root': { fontSize: 16 } }} />}
                      onClick={() => setOpcao(CODIGO_CONFIRMACAO_VIA_EMAIL)}
                      label={`E-mail para ${mascararEmail(clienteEncontrado.email)}`}
                    />
                  )}

                  {apiZapOk &&
                    clienteEncontrado.telefone
                      ?.filter((t) => t && t.trim() && t !== '00000000000')
                      .map((tel, index) => (
                        <FormControlLabel
                          key={index}
                          value={`${index + 2}`}
                          control={<Radio sx={{ '& .MuiSvgIcon-root': { fontSize: 16 } }} />}
                          onClick={() => {
                            setOpcao(CODIGO_CONFIRMACAO_VIA_WHATSAPP);
                            setTelefoneSelecionado(tel);
                          }}
                          label={`WhatsApp para ${mascararTelefone(tel)}`}
                        />
                      ))}
                </RadioGroup>
              </FormControl>

              {loading && (
                <Box sx={{ width: '100%', textAlign: 'center' }}>
                  <h2>Aguarde, o código está sendo enviado ...</h2>
                  <LinearProgress />
                </Box>
              )}
              {!pedirCodigo && (
                <Grid item xs={12} sx={{ textAlign: 'center', marginTop: 1 }}>
                  <Button fullWidth variant="contained" onClick={enviarCodigoVerificacao}>
                    <Typography fontSize={12}>Recuperar Senha</Typography>
                  </Button>
                </Grid>
              )}

              {pedirCodigo && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: '#f5f5f5',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <Typography sx={{ fontWeight: 'bold', color: '#d32f2f', fontSize: 13 }}>
                    Código expira em: {Math.floor(contador / 60)}:
                    {String(contador % 60).padStart(2, '0')}
                  </Typography>

                  <TextField
                    label="Código"
                    variant="outlined"
                    size="small"
                    value={codigoConfirmacao}
                    onChange={(e) => setCodigoConfirmacao(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <HistoryEduIcon fontSize="small" />
                        </InputAdornment>
                      )
                    }}
                    sx={{ width: '100%', maxWidth: 200 }}
                  />
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={confirmarCodigoVerificacao}
                    sx={{ mt: 1 }}
                  >
                    <Typography fontSize={12}>Recuperar Senha</Typography>
                  </Button>
                </Box>
              )}
            </Box>
          ) : (
            <Typography sx={{ color: 'red', textAlign: 'center', fontSize: 'small' }}>
              Nenhuma opção de envio disponível para este documento. Entre em contato com a nossa
              equipe.
            </Typography>
          ))}
      </Box>
    </Modal>
  );
};

export default ModalRecuperarSenha;
